import React, { useState, useEffect, useRef } from 'react';
import Icon from '../AppIcon';
import Button from './Button';

const NotificationCenter = ({ userRole = 'member' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const dropdownRef = useRef(null);

  const mockNotificationsByRole = {
    member: [
      {
        id: 1,
        type: 'class',
        title: 'Class Reminder',
        message: 'Your HIIT class starts in 30 minutes',
        timestamp: '2026-01-25T16:30:00',
        read: false,
        icon: 'Calendar',
        color: 'primary',
      },
      {
        id: 2,
        type: 'achievement',
        title: 'Achievement Unlocked!',
        message: 'You completed 10 workouts this month',
        timestamp: '2026-01-25T14:00:00',
        read: false,
        icon: 'Award',
        color: 'success',
      },
      {
        id: 3,
        type: 'payment',
        title: 'Payment Due',
        message: 'Your membership renewal is due in 3 days',
        timestamp: '2026-01-25T10:00:00',
        read: true,
        icon: 'CreditCard',
        color: 'warning',
      },
    ],
    staff: [
      {
        id: 1,
        type: 'checkin',
        title: 'Member Check-In',
        message: 'John Doe checked in at Front Desk',
        timestamp: '2026-01-25T16:45:00',
        read: false,
        icon: 'UserCheck',
        color: 'primary',
      },
      {
        id: 2,
        type: 'alert',
        title: 'Equipment Issue',
        message: 'Treadmill #3 reported as out of service',
        timestamp: '2026-01-25T15:30:00',
        read: false,
        icon: 'AlertTriangle',
        color: 'error',
      },
      {
        id: 3,
        type: 'member',
        title: 'New Member',
        message: 'Sarah Smith completed registration',
        timestamp: '2026-01-25T14:00:00',
        read: true,
        icon: 'UserPlus',
        color: 'success',
      },
    ],
    trainer: [
      {
        id: 1,
        type: 'session',
        title: 'Session Starting',
        message: 'Personal training with Mike Johnson in 15 minutes',
        timestamp: '2026-01-25T16:45:00',
        read: false,
        icon: 'Clock',
        color: 'primary',
      },
      {
        id: 2,
        type: 'cancellation',
        title: 'Class Cancelled',
        message: 'Client cancelled evening yoga session',
        timestamp: '2026-01-25T15:00:00',
        read: false,
        icon: 'XCircle',
        color: 'warning',
      },
    ],
    manager: [
      {
        id: 1,
        type: 'analytics',
        title: 'Daily Report Ready',
        message: 'View today\'s performance metrics',
        timestamp: '2026-01-25T17:00:00',
        read: false,
        icon: 'BarChart3',
        color: 'primary',
      },
      {
        id: 2,
        type: 'staff',
        title: 'Staff Schedule',
        message: 'Tomorrow\'s schedule needs approval',
        timestamp: '2026-01-25T14:30:00',
        read: false,
        icon: 'Users',
        color: 'accent',
      },
    ],
    owner: [
      {
        id: 1,
        type: 'financial',
        title: 'Revenue Update',
        message: 'Monthly revenue increased by 12%',
        timestamp: '2026-01-25T16:00:00',
        read: false,
        icon: 'TrendingUp',
        color: 'success',
      },
      {
        id: 2,
        type: 'location',
        title: 'New Location Alert',
        message: 'Downtown location reached capacity',
        timestamp: '2026-01-25T15:00:00',
        read: false,
        icon: 'Building2',
        color: 'warning',
      },
    ],
  };

  useEffect(() => {
    const roleNotifications = mockNotificationsByRole?.[userRole] || mockNotificationsByRole?.member;
    setNotifications(roleNotifications);
    setUnreadCount(roleNotifications?.filter(n => !n?.read)?.length);
  }, [userRole]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(event?.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleMarkAsRead = (notificationId) => {
    setNotifications(prev =>
      prev?.map(notif =>
        notif?.id === notificationId ? { ...notif, read: true } : notif
      )
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const handleMarkAllAsRead = () => {
    setNotifications(prev => prev?.map(notif => ({ ...notif, read: true })));
    setUnreadCount(0);
  };

  const handleClearAll = () => {
    setNotifications([]);
    setUnreadCount(0);
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  const getIconColor = (color) => {
    const colorMap = {
      primary: 'var(--color-primary)',
      success: 'var(--color-success)',
      warning: 'var(--color-warning)',
      error: 'var(--color-error)',
      accent: 'var(--color-accent)',
    };
    return colorMap?.[color] || colorMap?.primary;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-lg hover:bg-muted transition-colors duration-250 focus-ring"
        aria-label="Notifications"
      >
        <Icon name="Bell" size={22} />
        {unreadCount > 0 && (
          <span className="notification-badge">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>
      {isOpen && (
        <div className="dropdown-menu" style={{ width: '380px', maxWidth: '90vw' }}>
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <h3 className="text-base font-semibold text-popover-foreground">
              Notifications
            </h3>
            {notifications?.length > 0 && (
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <button
                    onClick={handleMarkAllAsRead}
                    className="text-xs text-primary hover:text-primary/80 transition-colors"
                  >
                    Mark all read
                  </button>
                )}
                <button
                  onClick={handleClearAll}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  Clear all
                </button>
              </div>
            )}
          </div>

          <div className="max-h-[400px] overflow-y-auto custom-scrollbar">
            {notifications?.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 px-4">
                <Icon name="Bell" size={48} color="var(--color-muted-foreground)" />
                <p className="mt-4 text-sm text-muted-foreground text-center">
                  No notifications yet
                </p>
              </div>
            ) : (
              notifications?.map((notification) => (
                <div
                  key={notification?.id}
                  className={`
                    flex gap-3 px-4 py-3 border-b border-border last:border-b-0
                    transition-colors duration-250 cursor-pointer
                    ${!notification?.read ? 'bg-primary/5' : 'hover:bg-muted'}
                  `}
                  onClick={() => handleMarkAsRead(notification?.id)}
                >
                  <div className="flex-shrink-0 mt-1">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${!notification?.read ? 'bg-primary/10' : 'bg-muted'}`}>
                      <Icon 
                        name={notification?.icon} 
                        size={20} 
                        color={getIconColor(notification?.color)}
                      />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <h4 className="text-sm font-medium text-popover-foreground truncate">
                        {notification?.title}
                      </h4>
                      {!notification?.read && (
                        <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0 mt-1"></div>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                      {notification?.message}
                    </p>
                    <span className="text-xs text-muted-foreground mt-1 inline-block">
                      {formatTimestamp(notification?.timestamp)}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>

          {notifications?.length > 0 && (
            <div className="px-4 py-3 border-t border-border">
              <Button
                variant="ghost"
                fullWidth
                className="text-sm"
              >
                View All Notifications
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;